import l, { type LoadableComponent } from "@loadable/component";
import { Close, PaymentOutlined, QrCodeScanner } from "@mui/icons-material";
import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { ArrayField, CreateBase, FormDataConsumer, required, SimpleForm, useCreateSuggestionContext, useStore } from "react-admin";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router";
import { AutocompleteInput, ReferenceInput, TextInput } from "../../components";
import { useAccount, useOrder, useOrderMutation, useWebsite } from "../../hooks";
import { orderToBuyerLocation, orderToCard, orderToZone, transformCard, transformLocation, type Card, type location, type Order, type zone } from "../../models";
import { OrderItemGrid } from "../order_item/grid";
import type { BarcodeScannerProps } from "./scanner";
import { LocationForm } from "../location/form";
import { CardForm } from "../card/form";
import { SocialAuthButton } from "../../pages/socialAuthButton";

export const BarcodeScanner: LoadableComponent<BarcodeScannerProps> = l(() => import("./scanner"));

export const CartForm = (): JSX.Element => {
    const w = useWebsite();
    const { id: accountId } = useAccount();
    const [orderId, setOrderId] = useStore<number>("orderId");
    const order = useOrder({ buyerId: accountId, sellerId: w.accountId, orderId })!;
    const setOrder = useOrderMutation();
    // const id = `${w.accountId}-${accountId}-${orderId}`;
    const nav = useNavigate();
    const { watch } = useFormContext();
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name && ["buyerLocationId", "zoneId", "cardId"].includes(name) && type === "change") {
                setOrder(order, { [name]: (value[name] ? value[name] : null) } as Partial<Order>)
            }
        });
        return () => subscription.unsubscribe()
    }, [watch, setOrder, order]);

    return <Grid container sx={{ flex: 1 }} spacing={1}>
        <Grid 
            size={{ md: 9, xs: 12 }}
            sx={_ => ({
                borderRadius: 1,
                padding: 1,
                display: "flex"
            })}
        >
            <Paper sx={{ display: "flex", flexDirection: "column", flexGrow: 1, padding: 1 }}>
            <BarcodeScanner accountId={w.accountId} open={open} setOpen={setOpen} />
            <ArrayField<Order> source="details">
                <OrderItemGrid editable first={<IconButton onClick={() => setOpen(!open)}><QrCodeScanner /></IconButton>} />
            </ArrayField>
            </Paper>
        </Grid>
        <Grid 
            size={{ md: 3, xs: 12 }} 
            sx={_ => ({
                borderRadius: 1,
                padding: 1,
                display: "flex"
            })}
        >
            <Paper sx={{ display: "flex", flexDirection: "column", flexGrow: 1, padding: 1 }}>
            <TextInput<Order>
                source="coupon"
                label="Promo Code"
                resettable
                onKeyUp={(e) => {
                    if (e.key === "Enter") {
                        setOrder(order, { coupon: (e.target as HTMLInputElement).value } as Partial<Order>);
                    }
                }}
                onChange={(e) => {
                    if (e === "") {
                        setOrder(order, { coupon: "" } as Partial<Order>);
                    }
                }}
                disabled={accountId === 0}
            />
            <ReferenceInput<Order, location>
                source="buyerLocationId"
                reference="location"
                filter={{ accountId, type: "Address", "locationId@not.eq": 0 } as any}
                map={orderToBuyerLocation}
            >
                <AutocompleteInput<location>
                    label="Address"
                    source="name" 
                    optionValue="locationId"
                    validate={required()}
                    filterToQuery={x => ({ name: x })}
                    debounce={250}
                    create={<CreateAddress accountId={accountId} />}
                    disabled={accountId === 0}
                />
            </ReferenceInput>
            <FormDataConsumer<Order>>
                {({ formData }) => (
                    <ReferenceInput<Order, zone>
                        source="zoneId"
                        reference="rpc/get_zone"
                        filter={{ "seller_id@": w.accountId, "buyer_id@": accountId, "location_id@": formData.buyerLocationId } as any}
                        map={orderToZone}
                    >
                        <AutocompleteInput<zone>
                            label="Shipping"
                            source="name" 
                            optionValue="zoneId"
                            validate={required()}
                            disabled={accountId === 0 || !formData.buyerLocationId}
                            filterToQuery={x => ({ name: x })}
                            matchSuggestion={(filter, choice: zone) => choice.name.toLowerCase().includes(filter.toLowerCase())}
                            inputText={(record: zone) => record.name}
                            optionText={(record: zone) => `${record.name} | $${record.fee}`}
                        />
                    </ReferenceInput>
                )}
            </FormDataConsumer>

            <ReferenceInput<Order, Card>
                source="cardId"
                reference="card"
                filter={{ accountId }}
                map={orderToCard}
            >
                <AutocompleteInput<Card>
                    label="Credit Card"
                    source="name" 
                    optionValue="cardId"
                    matchSuggestion={(filter, choice: Card) => choice.lastFour.toLowerCase().includes(filter.toLowerCase())}
                    inputText={(record: Card) => record.lastFour}
                    optionText={(x: Card) => `****-${x.lastFour}`}
                    validate={required()}
                    create={<CreateCard />}
                    disabled={accountId === 0}
                />
            </ReferenceInput>
        
            <TextInput<Order> source="notes" disabled={accountId === 0} />
            {accountId !== 0 ? <Button
                sx={{ width: "100%" }}
                disabled={!order?.buyerLocationId || !order.cardId || order?.total === 0}
                size="large"
                startIcon={<PaymentOutlined />}
                variant="contained"
                onClick={() => setOrder(order, { status: "Pending" }).then(_ => {
                    setOrderId(null as any);
                    nav("/", { viewTransition: true });
                })}
            >Pay</Button> : <SocialAuthButton provider="google" />}
            </Paper>
        </Grid>
    </Grid>;
};

const CreateAddress = ({ accountId }: { accountId: number }) => {
    const { filter, onCancel, onCreate } = useCreateSuggestionContext();

    return (
        <Dialog open onClose={onCancel}>
             <DialogTitle sx={{ m: 0, p: 2 }}>Shipping Address</DialogTitle>
             <IconButton
                aria-label="close"
                onClick={onCancel}
                sx={theme => ({
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <Close />
            </IconButton>
            <DialogContent sx={{ p: 0 }}>
                <CreateBase
                    redirect={false}
                    resource="location"
                    mutationOptions={{
                        onSuccess: onCreate,
                    }}
                    transform={transformLocation}
                >
                    <SimpleForm defaultValues={{ address: filter }}>
                        <LocationForm addressOnly type="Address" sx={{ height: "61vh" }} accountId={accountId} />
                    </SimpleForm>
                </CreateBase>
             </DialogContent>
        </Dialog>
    );
};

const CreateCard = () => {
    const { filter, onCancel, onCreate } = useCreateSuggestionContext();

    return (
        <Dialog open onClose={onCancel}>
             <DialogTitle sx={{ m: 0, p: 2 }}>Card Details</DialogTitle>
             <IconButton
                aria-label="close"
                onClick={onCancel}
                sx={theme => ({
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <Close />
            </IconButton>
            <DialogContent sx={{ p: 0 }}>
                <CreateBase
                    transform={transformCard}
                    redirect={false}
                    resource="card"
                    mutationOptions={{
                        onSuccess: onCreate,
                    }}
                >
                    <SimpleForm defaultValues={{ lastFour: filter }}>
                        <CardForm/>
                    </SimpleForm>
                </CreateBase>
             </DialogContent>
        </Dialog>
    );
};